import {
    BarController, BarElement, CategoryScale, Chart, LinearScale, TimeScale, Tooltip, LineController, LineElement,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import {
    format, parseISO, startOfToday, startOfMonth, startOfYear, addDays, addMonths, addYears, endOfMonth, endOfYear, getHours,
} from 'date-fns';
import { nl } from 'date-fns/locale';
import axios from 'axios';

const registerDynamicTariffWidgets = [];

class DynamicTariffsWidget {
    constructor(widget) {
        // Register elements:
        this.electricityChartElement = widget.querySelector('.electricity-chart .chart');
        this.electricityChartContainerElement = widget.querySelector('.electricity-chart .chart-container');
        this.gasChartElement = widget.querySelector('.gas-chart .chart');
        this.gasChartContainerElement = widget.querySelector('.gas-chart .chart-container');
        this.previousPeriodButton = widget.querySelector('.previous-period');
        this.nextPeriodButton = widget.querySelector('.next-period');
        this.periodLabel = widget.querySelector('.period-label');
        this.periodYearButton = widget.querySelector('.period-year');
        this.periodMonthButton = widget.querySelector('.period-month');
        this.periodDayButton = widget.querySelector('.period-day');
        this.splitTaxesCheckbox = widget.querySelector('.split-taxes');

        this.electricityAverage = widget.querySelector('.electricity-chart .average .value');
        this.electricityMin = widget.querySelector('.electricity-chart .min .value');
        this.electricityMax = widget.querySelector('.electricity-chart .max .value');

        this.gasRatesSummary = widget.querySelector('.gas-chart .rates-summary');
        this.gasAverage = widget.querySelector('.gas-chart .average .value');
        this.gasMin = widget.querySelector('.gas-chart .min .value');
        this.gasMax = widget.querySelector('.gas-chart .max .value');
        this.gasDayTariffs = widget.querySelector('.gas-chart .daily-tariffs');

        // Set default settings:
        this.periodType = 'day';
        this.period = startOfToday();

        // Create chart
        this.createCharts();

        // Register click handlers
        this.previousPeriodButton.addEventListener('click', () => {
            this.changePeriod(-1);
        });
        this.nextPeriodButton.addEventListener('click', () => {
            this.changePeriod(1);
        });

        this.periodYearButton.addEventListener('click', () => {
            this.changePeriodType('year');
        });
        this.periodMonthButton.addEventListener('click', () => {
            this.changePeriodType('month');
        });
        this.periodDayButton.addEventListener('click', () => {
            this.changePeriodType('day');
        });

        if (this.splitTaxesCheckbox) {
            this.splitTaxesCheckbox.addEventListener('change', () => {
                this.updateCharts();
            });
        }

        this.setActivePeriodButton = function(type) {
            const buttons = [this.periodDayButton, this.periodMonthButton, this.periodYearButton];
            buttons.forEach((btn) => btn.classList.remove('active'));

            if (type === 'day') {
                this.periodDayButton.classList.add('active');
            } else if (type === 'month') {
                this.periodMonthButton.classList.add('active');
            } else if (type === 'year') {
                this.periodYearButton.classList.add('active');
            }
        };

        // Setting period also updates data:
        this.changePeriodType('day');
    }

    checkPeriodAvailability() {
        const today = new Date();
        const minDate = new Date(2014, 1, 1); 
        const viewedDate = this.period;
    
        this.previousPeriodButton.classList.remove('inactive');
        this.nextPeriodButton.classList.remove('inactive');
    
        let prevPeriod, nextPeriod, maxPeriod;
    
        if (this.periodType === 'year') {
            prevPeriod = addYears(viewedDate, -1);
            nextPeriod = addYears(viewedDate, 1);
            maxPeriod = startOfYear(today); // je mag tot dit jaar
        } else if (this.periodType === 'month') {
            prevPeriod = addMonths(viewedDate, -1);
            nextPeriod = addMonths(viewedDate, 1);
            maxPeriod = startOfMonth(today); // je mag tot deze maand
        } else {
            prevPeriod = addDays(viewedDate, -1);
            nextPeriod = addDays(viewedDate, 1);
            maxPeriod = addDays(startOfToday(), 1); // je mag tot vandaag + 1 dag
        }
    
        if (prevPeriod < minDate) {
            this.previousPeriodButton.classList.add('inactive');
        }
    
        if (nextPeriod > maxPeriod) {
            this.nextPeriodButton.classList.add('inactive');
        }
    }
    

    changePeriod(amount) {
        if (this.periodType === 'day') {
            this.period = addDays(this.period, amount);
        } else if (this.periodType === 'month') {
            this.period = startOfMonth(addMonths(this.period, amount));
        } else if (this.periodType === 'year') {
            this.period = startOfYear(addYears(this.period, amount));
        }

        this.updateData();
        this.checkPeriodAvailability();
    }

    changePeriodType(type) {
        this.setActivePeriodButton(type);
        this.periodType = type;

        // Set default date based on period type
        if (type === 'year') {
            this.period = startOfYear(startOfToday());
        } else if (type === 'month') {
            this.period = startOfMonth(startOfToday());
        } else if (type === 'day') {
            this.period = startOfToday();
        }

        this.updateCharts();
        this.checkPeriodAvailability();
    }

    updateCharts() {
        let barThickness = 4;

        // Set axis labels
        if (this.periodType === 'day') {
            this.electricityChart.options.scales.x.time.unit = 'hour';
            this.electricityChart.options.scales.x.time.displayFormats.hour = 'H:mm';
            this.electricityChart.options.scales.x.time.tooltipFormat = 'd MMM HH:mm';
            this.electricityChart.options.scales.x.title.text = 'Uur';

            // Set bar thickness:
            barThickness = 4;

            // Hide gas chart and rates
            this.gasRatesSummary.classList.add('hidden');
            this.gasChartContainerElement.classList.add('hidden');

            // Show daily gas tariffs
            this.gasDayTariffs.classList.remove('hidden');
            this.gasDayTariffs.querySelector('.before-6-am .no-tariff')
                .classList
                .remove('hidden');
            this.gasDayTariffs.querySelector('.before-6-am .tariff')
                .classList
                .add('hidden');
            this.gasDayTariffs.querySelector('.after-6-am .no-tariff')
                .classList
                .remove('hidden');
            this.gasDayTariffs.querySelector('.after-6-am .tariff')
                .classList
                .add('hidden');
        } else {
            // Show gas chart and rates
            this.gasChartContainerElement.classList.remove('hidden');
            this.gasRatesSummary.classList.remove('hidden');
            this.gasDayTariffs.classList.add('hidden');

            if (this.periodType === 'month') {
                // Set bar thickness:
                barThickness = 8;

                this.electricityChart.options.scales.x.time.unit = 'day';
                this.electricityChart.options.scales.x.time.displayFormats.day = 'dd-MM';
                this.electricityChart.options.scales.x.time.tooltipFormat = 'EEEE d MMMM yyyy';
                this.electricityChart.options.scales.x.title.text = 'Dag';

                this.gasChart.options.scales.x.time.unit = 'day';
                this.gasChart.options.scales.x.time.displayFormats.day = 'dd-MM';
                this.gasChart.options.scales.x.time.tooltipFormat = 'EEEE d MMMM yyyy';
                this.gasChart.options.scales.x.title.text = 'Dag';
            } else if (this.periodType === 'year') {
                // Set bar thickness:
                barThickness = 12;

                this.electricityChart.options.scales.x.time.unit = 'month';
                this.electricityChart.options.scales.x.time.displayFormats.month = 'MMM';
                this.electricityChart.options.scales.x.time.tooltipFormat = 'MMMM yyyy';
                this.electricityChart.options.scales.x.title.text = 'Maand';

                this.gasChart.options.scales.x.time.unit = 'month';
                this.gasChart.options.scales.x.time.displayFormats.month = 'MMM';
                this.gasChart.options.scales.x.time.tooltipFormat = 'MMMM yyyy';
                this.gasChart.options.scales.x.title.text = 'Maand';
            }
        }

        this.electricityChart.data.datasets[0].barThickness = barThickness;
        this.electricityChart.data.datasets[1].barThickness = barThickness;
        this.electricityChart.data.datasets[2].barThickness = barThickness;
        this.gasChart.data.datasets[0].barThickness = barThickness;
        this.gasChart.data.datasets[1].barThickness = barThickness;
        this.gasChart.data.datasets[2].barThickness = barThickness;

        if (this.splitTaxesCheckbox && this.splitTaxesCheckbox.checked) {
            this.electricityChart.setDatasetVisibility(0, true);
            this.electricityChart.setDatasetVisibility(1, true);
            this.electricityChart.setDatasetVisibility(2, false);
        } else {
            this.electricityChart.setDatasetVisibility(0, false);
            this.electricityChart.setDatasetVisibility(1, false);
            this.electricityChart.setDatasetVisibility(2, true);
        }

        this.updateData();
    }

    updateData() {
        // Update period label
        if (this.periodType === 'day') {
            this.periodLabel.innerText = format(this.period, 'EEEE d MMMM yyyy', { locale: nl });
        } else if (this.periodType === 'month') {
            let label = format(this.period, 'MMMM yyyy', { locale: nl });
            label = label.charAt(0).toUpperCase() + label.slice(1);
            this.periodLabel.innerText = label;
        } else if (this.periodType === 'year') {
            this.periodLabel.innerText = format(this.period, 'yyyy', { locale: nl });
        }

        // Fetch data from API
        if (this.periodType === 'day') {
            // Day view: fetch data for the entire day (hourly precision)
            axios.get('/ajax/canary/market-price/hourly', {
                params: {
                    from: format(this.period, 'yyyy-MM-dd'),
                },
            })
                .then((response) => {
                    // Clear existing data and labels:
                    this.clearChartsData();
                    this.updateChartsWithData(response.data);

                    // Specifically update gas tariffs for this day:
                    this.gasDayTariffs.querySelector('.before-6-am .tariff')
                        .classList
                        .add('hidden');
                    this.gasDayTariffs.querySelector('.before-6-am .no-tariff')
                        .classList
                        .remove('hidden');
                    this.gasDayTariffs.querySelector('.after-6-am .tariff')
                        .classList
                        .add('hidden');
                    this.gasDayTariffs.querySelector('.after-6-am .no-tariff')
                        .classList
                        .remove('hidden');

                    if (response.data.gas) {
                        // Iterate over gas pricepoints to find the correct day tariffs
                        for (let i = 0; i < response.data.gas.pricepoints.length; i++) {
                            const pricepoint = response.data.gas.pricepoints[i];
                            const time = parseISO(pricepoint.time);

                            if (getHours(time) < 6) {
                                // Update tariff before 6 AM
                                this.gasDayTariffs.querySelector('.before-6-am .tariff .value')
                                    .innerText = pricepoint.price.priceWithTaxesVAT.toLocaleString('nl-NL', {
                                    style: 'currency',
                                    currency: 'EUR',
                                    minimumFractionDigits: 2,
                                });
                                this.gasDayTariffs.querySelector('.before-6-am .tariff')
                                    .classList
                                    .remove('hidden');
                                this.gasDayTariffs.querySelector('.before-6-am .no-tariff')
                                    .classList
                                    .add('hidden');
                            } else if (getHours(time) >= 6 && pricepoint.price) {
                                // Update tariff after 6 AM
                                this.gasDayTariffs.querySelector('.after-6-am .tariff .value')
                                    .innerText = pricepoint.price.priceWithTaxesVAT.toLocaleString('nl-NL', {
                                    style: 'currency',
                                    currency: 'EUR',
                                    minimumFractionDigits: 2,
                                });
                                this.gasDayTariffs.querySelector('.after-6-am .tariff')
                                    .classList
                                    .remove('hidden');
                                this.gasDayTariffs.querySelector('.after-6-am .no-tariff')
                                    .classList
                                    .add('hidden');
                            }
                        }
                    }
                })
                .catch((error) => {
                    console.error(error);
                });
        } else if (this.periodType === 'month') {
            // Month view: fetch data for the entire month (daily precision)
            const toDate = endOfMonth(this.period);

            axios.get('/ajax/canary/market-price/daily', {
                params: {
                    from: format(this.period, 'yyyy-MM-dd'),
                    to: format(toDate, 'yyyy-MM-dd'),
                },
            })
                .then((response) => {
                    // Clear existing data and labels:
                    this.clearChartsData();
                    this.updateChartsWithData(response.data);
                })
                .catch((error) => {
                    console.error(error);
                });
        } else if (this.periodType === 'year') {
            // Year view: fetch data for the entire year (daily precision)
            const toDate = endOfYear(this.period);

            axios.get('/ajax/canary/market-price/monthly', {
                params: {
                    from: format(this.period, 'yyyy-MM-dd'),
                    to: format(toDate, 'yyyy-MM-dd'),
                },
            })
                .then((response) => {
                    // Clear existing data and labels:
                    this.clearChartsData();
                    this.updateChartsWithData(response.data);
                })
                .catch((error) => {
                    console.error(error);
                });
        }

        this.electricityChart.update();
        this.gasChart.update();
    }

    /**
     * Clear all data from the charts (i.e., remove all data points and labels)
     */
    clearChartsData() {
        // Clear existing data and labels:
        while (this.electricityChart.data.labels.length > 0) {
            this.electricityChart.data.labels.pop();
        }

        for (let i = 0; i < this.electricityChart.data.datasets.length; i++) {
            while (this.electricityChart.data.datasets[i].data.length > 0) {
                this.electricityChart.data.datasets[i].data.pop();
            }
        }

        while (this.gasChart.data.labels.length > 0) {
            this.gasChart.data.labels.pop();
        }

        for (let i = 0; i < this.gasChart.data.datasets.length; i++) {
            while (this.gasChart.data.datasets[i].data.length > 0) {
                this.gasChart.data.datasets[i].data.pop();
            }
        }
    }

    updateChartsWithData(data) {
        this.clearChartsData();

        // Check whether we have electricity data at all
        if (data.electricity) {
            // Loop over electricity data
            for (let i = 0; i < data.electricity.pricepoints.length; i++) {
                const pricepoint = data.electricity.pricepoints[i];
                let priceObject;

                // Add timestamp as label:
                if (pricepoint.time) {
                    const time = parseISO(pricepoint.time);
                    this.electricityChart.data.labels.push(time);
                    priceObject = pricepoint.price;
                } else if (pricepoint.date) {
                    this.electricityChart.data.labels.push(parseISO(pricepoint.date));
                    priceObject = pricepoint.average;
                } else if (pricepoint.month) {
                    this.electricityChart.data.labels.push(parseISO(`${pricepoint.month}-01`));
                    priceObject = pricepoint.average;
                } else {
                    // eslint-disable-next-line no-console
                    console.warn('No time or date found in pricepoint', pricepoint);

                    // eslint-disable-next-line no-continue
                    continue;
                }

                // Add data
                this.electricityChart.data.datasets[0].data.push(priceObject.marketPrice);
                this.electricityChart.data.datasets[1].data.push(priceObject.priceWithTaxesVAT - priceObject.marketPrice);
                this.electricityChart.data.datasets[2].data.push(priceObject.priceWithTaxesVAT);
            }

            // Update avg/min/max
            this.electricityAverage.innerText = data.electricity.average.priceWithTaxesVAT.toLocaleString('nl-NL', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 2,
            });

            this.electricityMin.innerText = data.electricity.min.priceWithTaxesVAT.toLocaleString('nl-NL', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 2,
            });

            this.electricityMax.innerText = data.electricity.max.priceWithTaxesVAT.toLocaleString('nl-NL', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 2,
            });
        }

        // Check whether we have gas data at all
        if (data.gas) {
            // Loop over gas data
            for (let i = 0; i < data.gas.pricepoints.length; i++) {
                const pricepoint = data.gas.pricepoints[i];

                let priceObject;

                // Add timestamp as label:
                if (pricepoint.time) {
                    this.gasChart.data.labels.push(parseISO(pricepoint.time));
                    priceObject = pricepoint.price;
                } else if (pricepoint.date) {
                    this.gasChart.data.labels.push(parseISO(pricepoint.date));
                    priceObject = pricepoint.price;
                } else if (pricepoint.month) {
                    this.gasChart.data.labels.push(parseISO(`${pricepoint.month}-01`));
                    priceObject = pricepoint.average;
                } else {
                    // eslint-disable-next-line no-console
                    console.warn('No time or date found in pricepoint', pricepoint);

                    // eslint-disable-next-line no-continue
                    continue;
                }

                // Add data
                this.gasChart.data.datasets[0].data.push(priceObject.marketPrice);
                this.gasChart.data.datasets[1].data.push(priceObject.priceWithTaxesVAT - priceObject.marketPrice);
                this.gasChart.data.datasets[2].data.push(priceObject.priceWithTaxesVAT);
            }

            // Update avg/min/max
            this.gasAverage.innerText = data.gas.average.priceWithTaxesVAT.toLocaleString('nl-NL', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 2,
            });

            this.gasMin.innerText = data.gas.min.priceWithTaxesVAT.toLocaleString('nl-NL', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 2,
            });

            this.gasMax.innerText = data.gas.max.priceWithTaxesVAT.toLocaleString('nl-NL', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 2,
            });
        }

        this.electricityChart.update();
        this.gasChart.update();
    }

    createCharts() {
        Chart.defaults.font.size = 12;
        Chart.defaults.font.family = "'Greycliff CF', sans-serif";

        this.electricityChart = new Chart(
            this.electricityChartElement,
            {
                type: 'bar',
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    hoverBackgroundColor: 'rgba(0,100,132)',
                    scales: {
                        x: {
                            stacked: true,
                            grid: {
                                display: false,
                            },
                            type: 'time',
                            time: {
                                unit: 'hour',
                                tooltipFormat: 'dd MMM yy HH:mm',
                                displayFormats: {
                                    hour: 'H:mm',
                                },
                            },
                            adapters: {
                                date: {
                                    locale: nl,
                                },
                            },
                            title: {
                                display: false,
                                text: 'Tijdstip',
                            },
                        },
                        y: {
                            stacked: true,
                            padding: 0,
                            beginAtZero: true,
                            grace: '20%',
                            grid: {
                                display: true,
                                lineWidth: ({ tick }) => (tick.value === 0 ? 2 : 1),
                                color: ({ tick }) => (tick.value === 0 ? '#006484' : '#E5E5E5'),
                            },
                            border: {
                                display: false,
                            },
                            title: {
                                display: false,
                                text: 'Stroomprijs',
                            },
                            ticks: {
                                color: '#171717',
                                font: {
                                    size: 13,
                                    weight: 400,
                                },
                                major: {
                                    enabled: true,
                                },
                                stepSize: 0.1,
                                callback(value) {
                                    return value.toLocaleString('nl-NL', {
                                        style: 'currency',
                                        currency: 'EUR',
                                        minimumFractionDigits: 2,
                                    });
                                },
                            },
                        },
                    },
                    plugins: {
                        tooltip: {
                            enabled: true,
                        },
                        legend: {
                            display: false,
                        },
                    },
                },
                data: {
                    datasets: [
                        {
                            hidden: true,
                            stack: 'Split',
                            label: 'Marktprijs',
                            borderWidth: 0.5,
                            barThickness: 4,
                            borderColor: '#FFFFFF',
                            backgroundColor: 'rgb(73, 193, 220)',
                        },
                        {
                            hidden: true,
                            stack: 'Split',
                            label: 'Energiebelasting',
                            borderWidth: 0.5,
                            barThickness: 4,
                            borderColor: '#FFFFFF',
                            backgroundColor: 'rgb(73, 193, 220)',
                        },
                        {
                            stack: 'Totaal',
                            label: 'Totaalprijs',
                            borderWidth: 0.5,
                            barThickness: 4,
                            borderColor: '#FFFFFF',
                            backgroundColor: 'rgb(73, 193, 220)',
                        },
                    ],
                },
            },
        );

        // Create the gas chart
        this.gasChart = new Chart(
            this.gasChartElement,
            {
                type: 'bar',
                options: {
                    responsive: true,
                    maintainAspectRatio: true,
                    hoverBackgroundColor: 'rgba(0,100,132)',
                    scales: {
                        x: {
                            stacked: true,
                            grid: {
                                display: false,
                            },
                            type: 'time',
                            time: {
                                unit: 'hour',
                                tooltipFormat: 'dd MMM yy HH:mm',
                                displayFormats: {
                                    hour: 'H:mm',
                                },
                            },
                            adapters: {
                                date: {
                                    locale: nl,
                                },
                            },
                            title: {
                                display: false,
                                text: 'Tijdstip',
                            },
                        },
                        y: {
                            stacked: true,
                            padding: 0,
                            beginAtZero: true,
                            grace: '20%',
                            grid: {
                                display: true,
                                lineWidth: ({ tick }) => (tick.value === 0 ? 2 : 1),
                                color: ({ tick }) => (tick.value === 0 ? '#006484' : '#E5E5E5'),
                            },
                            border: {
                                display: false,
                            },
                            title: {
                                display: false,
                                text: 'Gasprijs',
                            },
                            ticks: {
                                color: '#171717',
                                font: {
                                    size: 13,
                                    weight: 400,
                                },
                                major: {
                                    enabled: true,
                                },
                                stepSize: 0.1,
                                callback(value) {
                                    return value.toLocaleString('nl-NL', {
                                        style: 'currency',
                                        currency: 'EUR',
                                        minimumFractionDigits: 2,
                                    });
                                },
                            },
                        },
                    },
                    plugins: {
                        tooltip: {
                            enabled: true,
                        },
                        legend: {
                            display: false,
                        },
                    },
                },
                data: {
                    datasets: [
                        {
                            stack: 'Split',
                            hidden: true,
                            label: 'Marktprijs',
                            barThickness: 4,
                            borderWidth: 0.5,
                            borderColor: 'rgb(255, 225, 225)',
                            backgroundColor: 'rgb(73, 193, 220)',
                        },
                        {
                            stack: 'Split',
                            hidden: true,
                            label: 'Energiebelasting',
                            barThickness: 4,
                            borderWidth: 0.5,
                            borderColor: 'rgb(255, 225, 225)',
                            backgroundColor: 'rgb(73, 193, 220)',
                        },
                        {
                            stack: 'Totaal',
                            label: 'Totaalprijs',
                            barThickness: 4,
                            borderWidth: 0.5,
                            borderColor: 'rgb(255, 225, 225)',
                            backgroundColor: 'rgb(73, 193, 220)',
                        },
                    ],
                },
            },
        );
    }
}

function registerDynamicTariffWidget(widget) {
    const widgetClass = new DynamicTariffsWidget(widget);
    registerDynamicTariffWidgets.push(widgetClass);
}

Chart.register(
    BarController,
    LineController,
    BarElement,
    LineElement,
    CategoryScale,
    LinearScale,
    TimeScale,
    Tooltip,
);

// Register widget for all instances on page (.dynamicTariffsWidget class)
const elem = document.querySelectorAll('.dynamicTariffsWidget .widget');

elem.forEach((widget) => {
    registerDynamicTariffWidget(widget);
});
